import { loadDemoFixture } from './fixture.js'

function parseTime(value) {
  if (value == null || value === '') return NaN
  const time = new Date(value).getTime()
  return Number.isNaN(time) ? NaN : time
}

export async function getDemoDataset() {
  const fixture = await loadDemoFixture()
  return Array.isArray(fixture.processed_dataset) ? fixture.processed_dataset : []
}

export async function queryDemoTimeRange({ start_time, end_time, columns } = {}) {
  const rows = await getDemoDataset()
  const startMs = parseTime(start_time)
  const endMs = parseTime(end_time)
  let filtered = rows.filter((row) => {
    const time = parseTime(row.datetime)
    if (Number.isNaN(time)) return false
    if (!Number.isNaN(startMs) && time < startMs) return false
    if (!Number.isNaN(endMs) && time > endMs) return false
    return true
  })

  if (Array.isArray(columns) && columns.length > 0) {
    filtered = filtered.map((row) => {
      const next = {}
      for (const column of columns) {
        if (Object.prototype.hasOwnProperty.call(row, column)) {
          next[column] = row[column]
        }
      }
      return next
    })
  }

  const hours = !Number.isNaN(startMs) && !Number.isNaN(endMs)
    ? (endMs - startMs) / 3600000
    : 0

  return {
    success: true,
    data: {
      filtered_data: filtered,
      total_rows: filtered.length,
      time_range_hours: hours,
      start_time,
      end_time
    }
  }
}

export async function queryDemoTimelineRange() {
  const rows = await getDemoDataset()
  if (!rows.length) {
    return { success: false, message: 'No demo dataset' }
  }
  return {
    success: true,
    data: {
      earliest: rows[0].datetime,
      latest: rows[rows.length - 1].datetime
    }
  }
}

function histogramEdges(values, width) {
  let min = Math.min(...values)
  let max = Math.max(...values)
  if (!(max > min)) {
    max = min + (width || 1)
  }
  if (width) {
    const domainMin = Math.floor(min / width) * width
    const domainMax = Math.ceil(max / width) * width
    const steps = Math.max(1, Math.round((domainMax - domainMin) / width))
    const edges = []
    for (let index = 0; index <= steps; index += 1) {
      edges.push(domainMin + index * width)
    }
    return edges
  }
  const count = 10
  const step = (max - min) / count
  return Array.from({ length: count + 1 }, (_, index) => min + index * step)
}

export async function queryDemoHistogram({ field, bins } = {}) {
  const rows = await getDemoDataset()
  const values = rows
    .map((row) => Number(row?.[field]))
    .filter((value) => Number.isFinite(value))

  if (!values.length) {
    return { success: false, message: `No valid numeric data for field ${field}` }
  }

  const width = Number(bins?.width) > 0 ? Number(bins.width) : null
  const edges = histogramEdges(values, width)
  const counts = new Array(Math.max(edges.length - 1, 0)).fill(0)
  for (const value of values) {
    if (value < edges[0] || value > edges[edges.length - 1]) continue
    let binIndex = edges.length - 2
    for (let index = 0; index < edges.length - 1; index += 1) {
      const isLast = index === edges.length - 2
      if (value >= edges[index] && (value < edges[index + 1] || (isLast && value <= edges[index + 1]))) {
        binIndex = index
        break
      }
    }
    counts[binIndex] += 1
  }

  return {
    success: true,
    data: {
      field,
      n: values.length,
      min: Math.min(...values),
      max: Math.max(...values),
      domain: [edges[0], edges[edges.length - 1]],
      binEdges: edges,
      binWidth: edges.length > 1 ? edges[1] - edges[0] : null,
      counts
    }
  }
}
